import React, { useMemo } from 'react';

import {
  ClickAwayListener,
  IconButton,
  makeStyles,
  Popper,
  Fade
} from '@material-ui/core';

import { clsx, colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import { Close } from '@carbon/icons-react';

import { PREVIOUS_PURCHASE_ORDER_ITEM } from '../assets';

import { useSelector } from '@store';

import { documentDateFormat, formatCurrency } from '@utils/formatter';
import useDocuments from '@hooks/useDocuments';

interface ItemProps {
  id: string;
  createdAt: string;
  quotationPrice: number;
  dueDate: string;
  isLatest: boolean;
  onClick?: (id: string) => void;
}

interface Props {
  title: string;
  items: ItemProps[];
  onClick: (id: string) => void;
}

const QuotationListDropdown = ({ title, items, onClick }: Props) => {
  const classes = useStyles();

  const { onUpdateDocumentsOptions } = useDocuments();

  const anchorId = useSelector(
    (state) => state.documents.options?.listAnchorId
  );

  const anchorEl = useMemo(
    () => (anchorId ? document.getElementById(anchorId) : null),
    [anchorId]
  );

  const open = Boolean(anchorEl);

  const onClose = () => {
    onUpdateDocumentsOptions({ listAnchorId: null });
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        className={classes.root}
        open={open}
        anchorEl={anchorEl}
        placement={'bottom-end'}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <div className={classes.paper}>
              <div className={classes.header}>
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  {title}
                </CustomizedTypography>

                <IconButton className="close" onClick={onClose}>
                  <Close />
                </IconButton>
              </div>

              <div className={classes.content}>
                {items.map((item, index) => (
                  <DropdownItem key={index} {...item} onClick={onClick} />
                ))}
              </div>
            </div>
          </Fade>
        )}
      </Popper>
    </ClickAwayListener>
  );
};

export default QuotationListDropdown;

const DropdownItem = ({
  id,
  createdAt,
  quotationPrice,
  dueDate,
  isLatest,
  onClick
}: ItemProps) => {
  const classes = useStyles();

  return (
    <div className={classes.item} onClick={() => onClick && onClick(id)}>
      <img width={56} height={56} src={PREVIOUS_PURCHASE_ORDER_ITEM} alt="" />

      {isLatest && (
        <div
          className={clsx(classes.chip, {
            latest: isLatest
          })}
        >
          최신
        </div>
      )}

      <div className={classes.itemContent}>
        <CustomizedTypography
          variant="body1"
          color="textPrimary"
          fontWeight="500"
        >
          {documentDateFormat(createdAt)}
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          fontWeight="500"
        >
          총견적&nbsp;・&nbsp;
          {formatCurrency(quotationPrice)}원
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          fontWeight="500"
        >
          납기일&nbsp;・&nbsp;발주일로부터 {dueDate}일
        </CustomizedTypography>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 16,
    maxWidth: 375,

    width: '100%',
    zIndex: 5000
  },
  paper: {
    borderRadius: 16,
    width: '100%',
    backgroundColor: colorSet.gray000,
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.1), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    position: 'relative',
    borderBottom: `1px solid ${colorSet.gray400}`,
    '& .close': {
      position: 'absolute',
      right: 12,
      padding: 0,
      '& svg': {
        width: 24,
        height: 24,
        color: colorSet.gray1000
      }
    }
  },
  content: {
    height: '100%',
    maxHeight: 520,
    overflowY: 'scroll'
  },
  item: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '20px 16px',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      backgroundColor: colorSet.gray100
    }
  },
  itemContent: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: 12
  },
  chip: {
    padding: '4px 8px',
    borderRadius: 100,
    position: 'absolute',
    top: 20,
    right: 16,
    '&.latest': {
      backgroundColor: colorSet.primary200,
      color: colorSet.primary500,
      ...theme.typography.caption
    },
    '&.reject': {
      backgroundColor: colorSet.errorBackGround,
      color: colorSet.errorMain,
      ...theme.typography.caption
    }
  }
}));
